import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom"; // ADICIONADO useLocation
import ProductContext from "../pages/pagesCardapio/ProductContext";
import Layout from "../components/Layout";
import SearchContainer from '../components/SearchContainer';
import { 
  FaPlusCircle, 
  FaTrashAlt, 
  FaSync, 
  FaArrowRight,
  FaMinus,
  FaPlus,
  FaCheck,
  FaReceipt,
  FaShoppingBag,
  FaUser,
  FaIdCard,
  FaSearch,
  FaFilter,
  FaQrcode,
  FaCamera
} from 'react-icons/fa';
import AppContext from '../api/AppContext';

const PedidoColaborador = () => {
  const navigate = useNavigate();
  const { products, categories, loading } = useContext(ProductContext);
  const { user: currentUser, token } = useContext(AppContext);
  
  const [quantities, setQuantities] = useState({});
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  
  // Estados para identificação do associado
  const [showMemberIdentification, setShowMemberIdentification] = useState(true); // Abrir automaticamente
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [isScanningTag, setIsScanningTag] = useState(false);
  const [tagData, setTagData] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [cameraError, setCameraError] = useState("");
  
  // Referências
  const searchInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Função para detectar tamanho da tela
  const getScreenSize = () => {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width >= 768 && width < 1024) return 'tablet';
    return 'desktop';
  };

  const [screenSize, setScreenSize] = useState(getScreenSize());
  const [searchQuery, setSearchQuery] = useState("");
  const [manualItems, setManualItems] = useState([]);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualName, setManualName] = useState("");
  const [manualPrice, setManualPrice] = useState("");

  useEffect(() => {
    const handleResize = () => setScreenSize(getScreenSize());
    window.addEventListener("resize", handleResize);
    
    return () => {
      window.removeEventListener("resize", handleResize);
      stopCamera();
    };
  }, []);

  // Focar no input de busca quando o modal abrir
  useEffect(() => {
    if (showMemberIdentification && searchInputRef.current) {
      setMemberSearchQuery('')
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [showMemberIdentification]);

  // Verificar se precisa identificar associado (staff ou admin)
  const needsMemberIdentification = currentUser && 
    (currentUser.role === 'staff' || currentUser.role === 'admin');


  // =============================================
  // LÓGICA DA CÂMERA E QR CODE
  // =============================================

  const startCamera = async () => {
    try {
      setCameraError("");
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "environment" } 
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Erro ao acessar câmera:', error);
      setCameraError('Não foi possível acessar a câmera. Verifique as permissões.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const handleQRCodeScan = () => {
    setShowQRScanner(true);
    setShowMemberIdentification(false);
    setTimeout(() => {
      startCamera();
    }, 100);
  };

  const closeQRScanner = () => {
    setShowQRScanner(false);
    // Não reabrir modal de identificação para membros
    if (needsMemberIdentification) {
      setShowMemberIdentification(true);
  }
    stopCamera();
    setCameraError("");
  };

  // Simular leitura de QR Code (em produção, usar uma biblioteca como jsQR)
  const simulateQRRead = () => {
    setIsScanningTag(true);
    // Simular leitura após 2 segundos
    setTimeout(() => {
      const qrData = "2409110290"; // Dado simulado do QR Code
      handleTagDetected(qrData);
      closeQRScanner();
    }, 2000);
  };

  // =============================================
  // INTEGRAÇÃO COM API
  // =============================================

  // Buscar membro pela tag
  const findMemberByTag = async (tagId) => {
    try {
      const response = await fetch(`https://app.olympicbarbacena.com.br/api/auth/check-tag/${tagId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Membro não encontrado');
      
      const result = await response.json();
      
      if (result.status === "success" && result.exists && result.user) {
        return {
          id: result.user.id,
          name: result.user.name,
          memberId: result.user.associadoId?.toString(),
          balance:  result.user.saldo, // Você pode ajustar conforme a API
          tagId: result.user.tag,
          cpf: result.user.cpf,
          numeroTitulo: result.user.numeroTitulo,
          role: result.user.role
        };
      } else {
        throw new Error('Tag não associada');
      }
    } catch (error) {
      console.error('Erro na busca por tag:', error);
      throw error;
    }
  };

  // Buscar membros por termo (busca manual)
  const searchMembers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      // Esta é uma busca simulada - você precisará implementar o endpoint real de busca
      const response = await fetch(`https://app.olympicbarbacena.com.br/api/members/search?q=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const results = await response.json();
        setSearchResults(results);
      } else {
        // Fallback para dados mock durante desenvolvimento
        setSearchResults(getMockSearchResults(query));
      }
    } catch (error) {
      console.error('Erro na busca de membros:', error);
      // Mock data para desenvolvimento
      setSearchResults(getMockSearchResults(query));
    } finally {
      setIsSearching(false);
    }
  };

  // Dados mock para desenvolvimento
  const getMockSearchResults = (query) => {
    const allMembers = [
      { id: '1', name: 'BENJAMIN FULLIN JUNIOR', memberId: '5276', balance: 150.75, tag: '2409110290' },
      { id: '2', name: 'MARIA SANTOS', memberId: '1234', balance: 89.50 },
      { id: '3', name: 'PEDRO OLIVEIRA', memberId: '5678', balance: 200.00 },
      { id: '4', name: 'ANA COSTA', memberId: '9012', balance: 75.25 },
    ];
    
    return allMembers.filter(member => 
      member.name.toLowerCase().includes(query.toLowerCase()) ||
      member.memberId.toLowerCase().includes(query.toLowerCase())
    );
  };

  // =============================================
  // FUNÇÕES AUXILIARES
  // =============================================

  const showNotification = (message, type = 'info') => {
    // Implementação simples de notificação
    alert(`${type.toUpperCase()}: ${message}`);
  };

  const handleTagDetected = async (tagId) => {
    if (!showMemberIdentification && !needsMemberIdentification) return;
    
    console.log('Processando tag:', tagId);
    setTagData(tagId);
    setIsScanningTag(true);

    try {
      // Buscar associado pela tag na API
      const member = await findMemberByTag(tagId);
      
      if (member) {
        setSelectedMember(member);
        setIsScanningTag(false);
        
        // Fechar modal automaticamente após 1 segundo
        setTimeout(() => {
          setShowMemberIdentification(false);
        }, 1000);
        
        // Feedback visual
        showNotification(`Associado identificado: ${member.name}`, 'success');
      } else {
        setIsScanningTag(false);
        showNotification('Tag não associada a nenhum membro', 'error');
      }
    } catch (error) {
      console.error('Erro ao buscar membro:', error);
      setIsScanningTag(false);
      showNotification('Erro ao identificar associado', 'error');
    }
  };

  const selectMemberFromSearch = (member) => {
    setSelectedMember(member);
    setSearchResults([]);
    setMemberSearchQuery("");
    setShowMemberIdentification(false);
    showNotification(`Associado selecionado: ${member.name}`, 'success');
  };

  // =============================================
  // FUNÇÕES EXISTENTES DO PEDIDO
  // =============================================

  const toggleDescription = (id) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const updateQuantity = (id, delta) => {
    setQuantities((prev) => ({
      ...prev,
      [id]: Math.max(0, (prev[id] || 0) + delta),
    }));
  };

  const addManualItem = () => {
    if (manualName.trim() && !isNaN(manualPrice) && parseFloat(manualPrice) > 0) {
      setManualItems([
        ...manualItems,
        {
          id: Date.now().toString(),
          name: manualName,
          price: parseFloat(manualPrice),
          quantity: 1,
        },
      ]);
      setManualName("");
      setManualPrice("");
      setShowManualForm(false);
    }
  };

  const removeManualItem = (id) => {
    setManualItems(manualItems.filter((item) => item.id !== id));
  };

  const removeProductItem = (id) => {
    setQuantities(prev => {
      const newQuantities = {...prev};
      delete newQuantities[id];
      return newQuantities;
    });
  };

    const handleConfirmOrder = async () => {
      // Verificar se precisa de identificação e se o associado foi selecionado
      if (needsMemberIdentification && !selectedMember) {
        setShowMemberIdentification(true);
        return;
      }


    // Preparar dados do pedido
  
      const orderData = {
      items: getAllOrderItems().map(item => ({
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        isManual: item.isManual || false,
        productCode: !item.isManual && products.find(p => p._id === item.id)?.productCode 
      })),
      totalAmount: totalAmount,
      member: needsMemberIdentification ? {
        id: selectedMember.id,
        name: selectedMember.name,
        memberId: selectedMember.memberId,
        balance: selectedMember.balance,
        tagId: selectedMember.tagId
      } : {
        id: currentUser.id||currentUser._id,
        name: currentUser.name,
        memberId: currentUser.associadoId,
        balance: currentUser.balance,
        tagId: currentUser.tag
      },
      processedBy: {
        id: currentUser.id||currentUser._id,
        name: currentUser.name,
        role: currentUser.role
      },
      timestamp: new Date().toISOString()
    };


    // try {
    //   // Enviar pedido para API
    //   const response = await fetch('https://app.olympicbarbacena.com.br/api/orders', {
    //     method: 'POST',
    //     headers: {
    //       'Authorization': `Bearer ${token}`,
    //       'Content-Type': 'application/json',
    //     },
    //     body: JSON.stringify(orderData),
    //   });
    //   console.log('orderData: ',orderData,response)

    //   if (!response.ok) throw new Error(response.data);

    //   const result = await response.json();
    //   console.log("Pedido confirmado:", result);
      
    //   showNotification('Pedido enviado com sucesso!', 'success');
      
    //   setShowConfirmModal(false);
    //   // Limpar o pedido após confirmação
    //   setQuantities({});
    //   setManualItems([]);
    //   setSelectedMember(null);
      
    // } catch (error) {
    //   console.error('Erro ao confirmar pedido:', error);
    //   showNotification('Erro ao enviar pedido', error.message);
    // }
    try {
      // Enviar pedido para API
      const response = await fetch('https://app.olympicbarbacena.com.br/api/orders', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });
      console.log('orderData: ', orderData, response);

      if (!response.ok) {
        // Tentar extrair a mensagem de erro do response
        const errorData = await response.json();
        throw new Error(errorData.message || `Erro HTTP: ${response.status}`);
      }

      const result = await response.json();
      console.log("Pedido confirmado:", result);
      
      showNotification('Pedido enviado com sucesso!', 'success');
      
      setShowConfirmModal(false);
      
      // Limpar o pedido após confirmação
      setQuantities({});
      setManualItems([]);
      
      // COMPORTAMENTO APÓS CONFIRMAÇÃO DO PEDIDO
      if (needsMemberIdentification) {
        // Se for admin/staff: reabrir modal de identificação e limpar membro selecionado
        setSelectedMember(null);
        setShowMemberIdentification(true);
      } else {
        // Se for member: redirecionar para tela de acompanhar pedido
        navigate("/member/acompanhar-pedido");
      }
      
    } catch (error) {
      console.error('Erro ao confirmar pedido:', error);
      showNotification('Erro ao enviar pedido', error.message);
    }
  };

  const getAllOrderItems = () => {
  const productItems = Object.entries(quantities)
      .filter(([_, qty]) => qty > 0)
      .map(([id, qty]) => {
        const product = products.find(p => p._id === id);
        return {
          id,
          name: product?.name || 'Produto não encontrado',
          price: product?.price || 0,
          quantity: qty,
          isManual: false,
          productCode: product?.productCode // Incluir código do produto se disponível
        };
      });

    const manualItemsList = manualItems.map(item => ({
      ...item,
      isManual: true,
      productCode: null // Itens manuais não têm código de produto
    }));

    return [...productItems, ...manualItemsList];
  };

  const totalAmount = getAllOrderItems().reduce(
    (total, item) => total + (item.price * item.quantity), 
    0
  );

  // Filtra produtos baseado na busca e categoria selecionada
  const filteredProducts = products.filter((item) => {
    const nameMatch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const categoryMatch = item.category.toLowerCase().includes(searchQuery.toLowerCase());
    const categoryFilter = selectedCategory ? item.category === selectedCategory : true;
    
    return (nameMatch || categoryMatch) && categoryFilter;
  });

  // Filtra categorias que têm produtos visíveis
  const visibleCategories = categories.filter((cat) => 
    filteredProducts.some((item) => item.category === cat.name)
  );

  const filteredCategories = visibleCategories.map((category) => ({
    ...category,
    items: filteredProducts.filter((item) => item.category === category.name),
  }));

  // Função para renderizar descrição com "ler mais"
  const renderDescription = (item) => {
    const description = item.description || '';
    const isExpanded = expandedDescriptions[item._id];
    const maxLength = 60;

    if (description.length <= maxLength || isExpanded) {
      return (
        <p style={styles.productDescription}>
          {description}
          {description.length > maxLength && (
            <button 
              onClick={() => toggleDescription(item._id)}
              style={styles.readMoreButton}
            >
              ler menos
            </button>
          )}
        </p>
      );
    }

    return (
      <p style={styles.productDescription}>
        {description.substring(0, maxLength)}...
        <button 
          onClick={() => toggleDescription(item._id)}
          style={styles.readMoreButton}
        >
          ler mais
        </button>
      </p>
    );
  };

  // Renderização da barra de filtros por categoria
  const renderCategoryFilters = () => {
    if (categories.length === 0) return null;

    return (
      <div style={styles.categoryFiltersContainer}>
        <div style={styles.categoryFilters}>
          {categories.map((category) => (
            <button
              key={category._id}
              onClick={() => setSelectedCategory(
                selectedCategory === category.name ? "" : category.name
              )}
              style={{
                ...styles.categoryFilterButton,
                ...(selectedCategory === category.name ? styles.categoryFilterButtonActive : {})
              }}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Renderização mobile em lista
  const renderMobileProductList = () => {
    return (
      <div style={styles.mobileProductList}>
        {loading ? (
          <div style={styles.loadingContainer}>
            <FaSync style={styles.loadingIcon} spin />
            <p>Carregando cardápio...</p>
          </div>
        ) : (
          filteredCategories.map((group) => (
            <div key={group._id} style={styles.categorySection}>
              <h3 style={styles.categoryTitle}>{group.name}</h3>
              <div style={styles.mobileProductsList}>
                {group.items.map((item) => (
                  <div key={item._id} style={styles.mobileProductItem}>
                    <div style={styles.mobileProductImageContainer}>
                      <img
                        src={
                          item.image
                            ? `https://cardapio.olympicbarbacena.com.br${item.image}`
                            : "https://cardapio.olympicbarbacena.com.br/uploads/1737063857910.png"
                        }
                        alt={item.name}
                        style={styles.mobileProductImage}
                      />
                    </div>
                    <div style={styles.mobileProductInfo}>
                      <h4 style={styles.mobileProductName}>{item.name}</h4>
                      {renderDescription(item)}
                      <div style={styles.mobileProductFooter}>
                        <span style={styles.mobileProductPrice}>R$ {item.price?.toFixed(2) || "—"}</span>
                        <div style={styles.mobileQuantityControl}>
                          <button 
                            onClick={() => updateQuantity(item._id, -1)}
                            style={{
                              ...styles.mobileQuantityButton,
                              ...(!quantities[item._id] ? styles.disabledQuantityButton : {})
                            }}
                            disabled={!quantities[item._id]}
                          >
                            <FaMinus />
                          </button>
                          <span style={styles.mobileQuantityValue}>
                            {quantities[item._id] || 0}
                          </span>
                          <button 
                            onClick={() => updateQuantity(item._id, 1)}
                            style={styles.mobileQuantityButton}
                          >
                            <FaPlus />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  // Renderização normal (tablet/desktop)
  const renderNormalProductGrid = () => {
    return (
      <div style={styles.productsColumn}>
        {loading ? (
          <div style={styles.loadingContainer}>
            <FaSync style={styles.loadingIcon} spin />
            <p>Carregando cardápio...</p>
          </div>
        ) : (
          filteredCategories.map((group) => (
            <div key={group._id} style={styles.categorySection}>
              <h3 style={styles.categoryTitle}>{group.name}</h3>
              <div style={{
                ...styles.productsGrid,
                ...styles[screenSize + 'Grid'],
              }}>
                {group.items.map((item) => (
                  <div key={item._id} style={styles.productCard}>
                    <div style={styles.productImageContainer}>
                      <img
                        src={
                          item.image
                            ? `https://cardapio.olympicbarbacena.com.br${item.image}`
                            : "https://cardapio.olympicbarbacena.com.br/uploads/1737063857910.png"
                        }
                        alt={item.name}
                        style={styles.productImage}
                      />
                      <div style={styles.quantityOverlay}>
                        <div style={styles.quantityControl}>
                          <button 
                            onClick={() => updateQuantity(item._id, -1)}
                            style={styles.quantityButton}
                            disabled={!quantities[item._id]}
                          >
                            <FaMinus />
                          </button>
                          <span style={styles.quantityValue}>{quantities[item._id] || 0}</span>
                          <button 
                            onClick={() => updateQuantity(item._id, 1)}
                            style={styles.quantityButton}
                          >
                            <FaPlus />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div style={styles.productInfo}>
                      <h4 style={styles.productName}>{item.name}</h4>
                      {renderDescription(item)}
                      <div style={styles.productFooter}>
                        <span style={styles.productPrice}>R$ {item.price?.toFixed(2) || "—"}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  // =============================================
  // COMPONENTES DE RENDERIZAÇÃO
  // =============================================

  const renderQRScannerModal = () => {
    if (!showQRScanner) return null;

    return (
      <div style={styles.modalOverlay}>
        <div style={styles.qrModalContent}>
          <div style={styles.modalHeader}>
            <FaQrcode style={styles.modalIcon} />
            <h3 style={styles.modalTitle}>Ler QR Code</h3>
            <button 
              onClick={closeQRScanner}
              style={styles.closeModalButton}
            >
              ×
            </button>
          </div>
          
          <div style={styles.qrScannerContainer}>
            <div style={styles.cameraPreview}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                style={styles.cameraVideo}
              />
              <div style={styles.qrOverlay}>
                <div style={styles.qrFrame} />
                <p style={styles.qrInstruction}>
                  Posicione o QR Code dentro do quadro
                </p>
              </div>
            </div>
            
            {cameraError && (
              <div style={styles.cameraError}>
                {cameraError}
              </div>
            )}
          </div>

          <div style={styles.qrModalActions}>
            <button 
              onClick={closeQRScanner}
              style={styles.cancelButton}
            >
              Cancelar
            </button>
            <button 
              onClick={simulateQRRead}
              disabled={isScanningTag}
              style={styles.scanButton}
            >
              <FaCamera />
              {isScanningTag ? 'Lendo...' : 'Simular Leitura'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderMemberIdentificationModal = () => {
    if (!showMemberIdentification || !needsMemberIdentification) return null;

    return (
      <div style={styles.modalOverlay}>
        <div style={styles.memberModalContent}>
          <div style={styles.modalHeader}>
            <FaIdCard style={styles.modalIcon} />
            <h3 style={styles.modalTitle}>Identificar Associado</h3>
            <button 
              onClick={() => setShowMemberIdentification(false)}
              style={styles.closeModalButton}
            >
              ×
            </button>
          </div>
          
          <div style={styles.modalBody}>
            <div style={styles.tagStatus}>
              <div style={styles.tagStatusIndicator}>
                <div style={{
                  ...styles.statusLight,
                  ...(isScanningTag ? styles.statusLightActive : styles.statusLightReady)
                }} />
                <span style={styles.statusText}>
                  {isScanningTag ? 'Processando...' : 'Pronto para Leitura'}
                </span>
              </div>
              {tagData && (
                <div style={styles.tagData}>
                  Última Tag: {tagData}
                </div>
              )}
            </div>

            <div style={styles.identificationOptions}>
              {/* Instruções */}
              <div style={styles.instructions}>
                <p style={styles.instructionText}>
                  <strong>Método 1 (RFID):</strong> Aproxime a carteirinha do leitor - o código será inserido automaticamente no campo abaixo
                </p>
                <p style={styles.instructionText}>
                  <strong>Método 2 (QR Code):</strong> Clique no botão da câmera para ler o QR Code
                </p>
                <p style={styles.instructionText}>
                  <strong>Método 3 (Busca):</strong> Digite o nome ou ID do associado
                </p>
              </div>

              {/* Campo para entrada da tag */}
              <div style={styles.tagInputContainer}>
                <h4 style={styles.searchTitle}>Código da Tag</h4>
                <div style={styles.searchContainer}>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="O código da tag aparecerá aqui automaticamente..."
                    value={memberSearchQuery}
                    onChange={(e) => {
                      setMemberSearchQuery(e.target.value);
                      // Quando o usuário digitar manualmente ou o sensor enviar dados
                      if (e.target.value.length > 0) {
                        searchMembers(e.target.value);
                      }
                    }}
                    onKeyPress={(e) => {
                      // Quando pressionar Enter no campo de tag
                      if (e.key === 'Enter' && memberSearchQuery.trim().length > 0) {
                        handleTagDetected(memberSearchQuery);
                      }
                    }}
                    style={styles.tagInput}
                    autoFocus
                  />
                  <button 
                    onClick={handleQRCodeScan}
                    style={styles.qrButton}
                    title="Ler QR Code"
                  >
                    <FaQrcode />
                  </button>
                </div>
              </div>

              {/* Resultados da Busca */}
              {isSearching && (
                <div style={styles.loadingResults}>
                  <FaSync style={styles.loadingIcon} spin />
                  Buscando...
                </div>
              )}

              {searchResults.length > 0 && (
                <div style={styles.searchResults}>
                  <h4 style={styles.resultsTitle}>Resultados da Busca</h4>
                  {searchResults.map(member => (
                    <div 
                      key={member.id}
                      style={styles.searchResultItem}
                      onClick={() => selectMemberFromSearch(member)}
                    >
                      <FaUser style={styles.resultIcon} />
                      <div style={styles.resultInfo}>
                        <span style={styles.resultName}>{member.name}</span>
                        <span style={styles.resultId}>ID: {member.memberId}</span>
                        {member.tag && (
                          <span style={styles.resultTag}>Tag: {member.tag}</span>
                        )}
                      </div>
                      <span style={styles.resultBalance}>
                        R$ {member.balance?.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={styles.modalActions}>
            <button 
              onClick={() => setShowMemberIdentification(false)}
              style={styles.cancelButton}
            >
              Cancelar
            </button>
            <button 
              onClick={() => memberSearchQuery && handleTagDetected(memberSearchQuery)}
              disabled={!memberSearchQuery || isScanningTag}
              style={styles.confirmButton}
            >
              <FaCheck />
              Confirmar
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderMemberInfoInModal = () => {
    if (!needsMemberIdentification) return null;

    return (
      <div style={styles.memberInfoSection}>
        <h4 style={styles.memberInfoTitle}>Associado</h4>
        {selectedMember ? (
          <div style={styles.memberInfo}>
            <FaUser style={styles.memberIcon} />
            <div style={styles.memberDetails}>
              <span style={styles.memberName}>{selectedMember.name}</span>
              <span style={styles.memberId}>ID: {selectedMember.memberId}</span>
              {selectedMember.tagId && (
                <span style={styles.memberTag}>Tag: {selectedMember.tagId}</span>
              )}
              <span style={styles.memberBalance}>Saldo: R$ {selectedMember.balance?.toFixed(2)}</span>
            </div>
            <button 
              onClick={() => {
                setShowConfirmModal(false);
                setShowMemberIdentification(true);
              }}
              style={styles.changeMemberButton}
            >
              Alterar
            </button>
          </div>
        ) : (
          <div style={styles.noMemberSelected}>
            <p style={styles.noMemberText}>Nenhum associado selecionado</p>
            <button 
              onClick={() => {
                setShowConfirmModal(false);
                setShowMemberIdentification(true);
              }}
              style={styles.selectMemberButton}
            >
              <FaUser style={styles.memberIcon} />
              Identificar Associado
            </button>
          </div>
        )}
      </div>
    );
  };

  const handleModalConfirmOrder = () => {
    if (needsMemberIdentification && !selectedMember) {
      setShowConfirmModal(false);
      setShowMemberIdentification(true);
    } else {
      handleConfirmOrder();
    }
  };

  return (
    <Layout title="Enviar Pedido" icon={FaShoppingBag}>
      <div style={{
        ...styles.container,
        ...styles[screenSize + 'Container'],
      }}>
        {/* Header com busca e botão de item manual */}
        <div style={styles.header}>
         <SearchContainer 
          placeholder="Buscar item..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchContainer}
        />
          {/* <div style={styles.searchContainer}>
            <input
              type="text"
              placeholder="Buscar item..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.textInput}
            />
          </div> */}
          
          {/* <button 
            onClick={() => setShowManualForm(!showManualForm)}
            style={styles.manualItemButton}
          >
            <FaPlusCircle style={styles.manualItemIcon} />
            Item Manual
          </button> */}
        </div>

        {/* Barra de filtros por categoria */}
        {renderCategoryFilters()}

        {/* Indicador de associado para staff/admin */}
        {needsMemberIdentification && (
          <div style={styles.memberIndicator}>
            <div style={styles.memberIndicatorContent}>
              <FaUser style={styles.memberIndicatorIcon} />
              <div style={styles.memberIndicatorInfo}>
                <span style={styles.memberIndicatorLabel}>
                  Associado: 
                </span>
                <span style={styles.memberIndicatorValue}>
                  {selectedMember ? selectedMember.name : 'Não identificado'}
                </span>
              </div>
              <button 
                onClick={() => setShowMemberIdentification(true)}
                style={styles.identifyButton}
              >
                <FaIdCard />
                Identificar
              </button>
            </div>
          </div>
        )}

        {/* Formulário de item manual */}
        {showManualForm && (
          <div style={styles.manualFormOverlay}>
            <div style={styles.manualForm}>
              <div style={styles.formHeader}>
                <h4 style={styles.formTitle}>Adicionar Item Manual</h4>
                <button 
                  onClick={() => setShowManualForm(false)}
                  style={styles.closeFormButton}
                >
                  ×
                </button>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.inputLabel}>Nome do Item</label>
                <input
                  placeholder="Ex: Suco Natural"
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  style={styles.textInput}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.inputLabel}>Preço (R$)</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={manualPrice}
                  onChange={(e) => setManualPrice(e.target.value)}
                  style={styles.textInput}
                />
              </div>
              <button 
                onClick={addManualItem} 
                style={{
                  ...styles.confirmButton,
                  ...(!manualName.trim() || isNaN(manualPrice) || parseFloat(manualPrice) <= 0 ? styles.disabledButton : {})
                }}
                disabled={!manualName.trim() || isNaN(manualPrice) || parseFloat(manualPrice) <= 0}
              >
                Adicionar Item
              </button>
            </div>
          </div>
        )}

        {/* Modal de identificação do associado */}
        {renderMemberIdentificationModal()}

        {/* Modal de scanner de QR Code */}
        {renderQRScannerModal()}

        {/* Modal de confirmação do pedido */}
        {showConfirmModal && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <div style={styles.modalHeader}>
                <FaReceipt style={styles.modalIcon} />
                <h3 style={styles.modalTitle}>Confirmar Pedido</h3>
                <button 
                  onClick={() => setShowConfirmModal(false)}
                  style={styles.closeModalButton}
                >
                  ×
                </button>
              </div>
              
              <div style={styles.modalBody}>
                {/* Informações do associado */}
                {renderMemberInfoInModal()}
                
                <h4 style={styles.orderSummaryTitle}>Resumo do Pedido</h4>
                <div style={styles.orderSummary}>
                  {getAllOrderItems().map((item, index) => (
                    <div key={index} style={styles.summaryItem}>
                      <div style={styles.summaryItemInfo}>
                        <span style={styles.summaryItemName}>{item.name}</span>
                        <div style={styles.summaryItemControls}>
                          <button 
                            onClick={() => item.isManual ? removeManualItem(item.id) : updateQuantity(item.id, -1)}
                            style={{
                              ...styles.summaryQuantityButton,
                              ...(item.quantity <= 1 ? styles.disabledQuantityButton : {})
                            }}
                            disabled={item.quantity <= 1 && !item.isManual}
                          >
                            <FaMinus />
                          </button>
                          <span style={styles.summaryItemQuantity}>x{item.quantity}</span>
                          <button 
                            onClick={() => item.isManual ? 
                              setManualItems(manualItems.map(i => i.id === item.id ? {...i, quantity: i.quantity + 1} : i)) : 
                              updateQuantity(item.id, 1)}
                            style={styles.summaryQuantityButton}
                          >
                            <FaPlus />
                          </button>
                        </div>
                      </div>
                      <div style={styles.summaryItemRight}>
                        <span style={styles.summaryItemPrice}>
                          R$ {(item.price * item.quantity).toFixed(2)}
                        </span>
                        <button
                          onClick={() => item.isManual ? removeManualItem(item.id) : removeProductItem(item.id)}
                          style={styles.summaryItemRemove}
                        >
                          <FaTrashAlt />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div style={styles.modalTotal}>
                  <span style={styles.modalTotalLabel}>TOTAL</span>
                  <span style={styles.modalTotalAmount}>R$ {totalAmount.toFixed(2)}</span>
                </div>
              </div>

              <div style={styles.modalActions}>
                <button 
                  onClick={() => setShowConfirmModal(false)}
                  style={styles.cancelOrderButton}
                >
                  Cancelar
                </button>
                <button 
                  onClick={handleModalConfirmOrder}
                  style={styles.confirmOrderButton}
                >
                  <FaCheck style={styles.confirmIcon} />
                  Confirmar Pedido
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Área principal com duas colunas: produtos e pedido */}
        <div style={{
          ...styles.mainContent,
          ...styles[screenSize + 'Content'],
        }}>
          {/* Coluna de produtos - Mobile em lista, outros em grid */}
          {screenSize === 'mobile' ? renderMobileProductList() : renderNormalProductGrid()}

          {/* Coluna do pedido - No mobile fica fixa na parte de baixo */}
          <div style={{
            ...styles.orderColumn,
            ...styles[screenSize + 'OrderColumn'],
          }}>
            <div style={styles.orderContainer}>
              {/* <h3 style={styles.orderTitle}>Seu Pedido</h3> */}
              
              {screenSize !== 'mobile' && (
                <div style={styles.orderItemsScroll}>
                  {getAllOrderItems().length > 0 ? (
                    <div style={styles.orderItemsList}>
                      {getAllOrderItems().map((item) => (
                        <div key={item.id} style={styles.orderItem}>
                          <div style={styles.orderItemInfo}>
                            <span style={styles.orderItemName}>
                              {item.name}
                            </span>
                            <div style={styles.orderItemDetails}>
                              <span style={styles.orderItemQuantity}>
                                {item.quantity}x
                              </span>
                              <span style={styles.orderItemPrice}>
                                R$ {(item.price * item.quantity).toFixed(2)}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => item.isManual ? removeManualItem(item.id) : removeProductItem(item.id)}
                            style={styles.orderItemRemove}
                          >
                            <FaTrashAlt />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={styles.emptyMessage}>Nenhum item adicionado ainda</p>
                  )}
                </div>
              )}

              {/* No mobile, mostra apenas total e botão */}
              <div style={screenSize === 'mobile' ? styles.mobileOrderFooter : styles.orderTotal}>
                <span style={styles.totalLabel}>TOTAL</span>
                <span style={styles.totalAmount}>R$ {totalAmount.toFixed(2)}</span>
              </div>

              <button 
                onClick={() => setShowConfirmModal(true)}
                style={{
                  ...styles.checkoutButton,
                  ...(getAllOrderItems().length === 0 ? styles.disabledCheckoutButton : {})
                }}
                disabled={getAllOrderItems().length === 0}
              >
                {screenSize === 'mobile' ? 'Ver Pedido' : 'Enviar Pedido'}
                <FaArrowRight style={styles.checkoutIcon} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const styles = {
  container: {
    fontFamily: 'Montserrat, sans-serif',
    minHeight: "100vh",
    boxSizing: "border-box",
    width: "100%",
    backgroundColor: "#f9f8faff",
  },
  mobileContainer: {
    maxWidth: "100%",
    paddingBottom: "102px",
    backgroundColor: "rgb(240, 243, 255)",
  },
  tabletContainer: {
    maxWidth: "100%",
    padding: "20px",
  },
  desktopContainer: {
    maxWidth: "1400px",
    margin: "0 auto",
    padding: "20px",
  },
  header: {
    gap: "15px",
    alignItems: "center",
    marginBottom: "20px",
    flexWrap: "wrap",
    padding: "0 20px",
    paddingTop: "20px",
  },
  searchContainer: {
    width: "130%",
    padding: "12px 20px 12px 40px",
    borderRadius: "100px",
  },
  textInput: {
    width: "100%",
    padding: "14px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
    fontSize: "14px",
    transition: "all 0.3s ease",
  },
  manualItemButton: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "#3f51b5",
    color: "white",
    border: "none",
    padding: "12px 20px",
    borderRadius: "8px",
    fontWeight: "500",
    cursor: "pointer",
    fontSize: "14px",
    whiteSpace: "nowrap",
  },
  manualItemIcon: {
    fontSize: "16px",
  },
  manualFormOverlay: {
    position: "fixed",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: "1000",
    padding: "17px",
  },
  manualForm: {
    backgroundColor: "#ffffff",
    padding: "25px",
    borderRadius: "12px",
    boxShadow: "0 5px 20px rgba(0,0,0,0.2)",
    width: "100%",
    maxWidth: "400px",
  },
  formHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  formTitle: {
    color: "#3f51b5",
    margin: "0",
    fontSize: "18px",
    fontWeight: "600",
  },
  closeFormButton: {
    backgroundColor: "transparent",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    color: "#757575",
    padding: "0",
    lineHeight: "1",
  },
  inputGroup: {
    marginBottom: "20px",
  },
  inputLabel: {
    display: "block",
    marginBottom: "8px",
    fontSize: "14px",
    color: "#616161",
    fontWeight: "500",
  },
  confirmButton: {
    backgroundColor: "#4caf50",
    color: "#fff",
    border: "none",
    padding: "14px 20px",
    borderRadius: "8px",
    fontWeight: "500",
    cursor: "pointer",
    fontSize: "14px",
    width: "100%",
    transition: "all 0.3s ease",
    fontFamily: "Montserrat, sans-serif",
  },
  disabledButton: {
    backgroundColor: "#cccccc",
    cursor: "not-allowed",
  },
  modalOverlay: {
    position: "fixed",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    backgroundColor: "rgba(0,0,0,0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: "2000",
    padding: "20px",
  },
  modalContent: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "25px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
    width: "100%",
    maxWidth: "500px",
    maxHeight: "90vh",
    overflow: "auto",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "20px",
    position: "relative",
  },
  modalIcon: {
    fontSize: "24px",
    color: "#4caf50",
    marginRight: "10px",
  },
  modalTitle: {
    color: "#3f51b5",
    margin: "0",
    fontSize: "22px",
    fontWeight: "600",
  },
  closeModalButton: {
    position: "absolute",
    right: "0",
    top: "0",
    backgroundColor: "transparent",
    border: "none",
    fontSize: "28px",
    cursor: "pointer",
    color: "#757575",
    padding: "0",
    lineHeight: "1",
  },
  modalBody: {
    marginBottom: "25px",
  },
  orderSummaryTitle: {
    color: "#333",
    margin: "0 0 15px 0",
    fontSize: "18px",
    fontWeight: "600",
    textAlign: "center",
  },
  orderSummary: {
    maxHeight: "300px",
    overflow: "auto",
    marginBottom: "20px",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "10px",
  },
  summaryItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px",
    borderBottom: "1px solid #f0f0f0",
  },
  summaryItemInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    flex: "1",
  },
  summaryItemName: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#333",
  },
  summaryItemControls: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  summaryQuantityButton: {
    backgroundColor: "#3f51b5",
    color: "white",
    border: "none",
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "bold",
    transition: "all 0.2s ease",
  },
  disabledQuantityButton: {
    backgroundColor: "#cccccc",
    cursor: "not-allowed",
  },
  summaryItemQuantity: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
    minWidth: "30px",
    textAlign: "center",
  },
  summaryItemRight: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "8px",
  },
  summaryItemPrice: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#4caf50",
  },
  summaryItemRemove: {
    backgroundColor: "transparent",
    border: "none",
    color: "#f44336",
    cursor: "pointer",
    fontSize: "14px",
    padding: "4px",
  },
  modalTotal: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "15px 0",
    borderTop: "2px solid #e0e0e0",
  },
  modalTotalLabel: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#333",
  },
  modalTotalAmount: {
    fontSize: "15px",
    fontWeight: "700",
    color: "#4caf50",
  },
  modalActions: {
    display: "flex",
    gap: "15px",
  },
  cancelOrderButton: {
    backgroundColor: "#f5f5f5",
    color: "#757575",
    border: "none",
    padding: "14px 20px",
    borderRadius: "8px",
    fontWeight: "500",
    cursor: "pointer",
    fontSize: "14px",
    flex: "1",
    transition: "all 0.3s ease",
  },
  confirmOrderButton: {
    backgroundColor: "#4caf50",
    color: "#fff",
    border: "none",
    padding: "14px 20px",
    borderRadius: "8px",
    fontWeight: "500",
    cursor: "pointer",
    fontSize: "14px",
    flex: "1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    transition: "all 0.3s ease",
  },
  confirmIcon: {
    fontSize: "16px",
  },
  mainContent: {
    display: "grid",
    gap: "20px",
  },
  mobileContent: {
    gridTemplateColumns: "1fr",
  },
  tabletContent: {
    gridTemplateColumns: "2fr 1fr",
    height: "calc(100vh - 120px)",
  },
  desktopContent: {
    gridTemplateColumns: "3fr 1fr",
    height: "calc(100vh - 120px)",
  },
  mobileProductList: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    overflow: "auto",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  },
  mobileProductsList: {
    display: "flex",
    flexDirection: "column",
    gap: "0",
  },
  mobileProductItem: {
    display: "flex",
    padding: "15px",
    borderBottom: "1px solid #f0f0f0",
    gap: "15px",
    alignItems: "flex-start",
  },
  mobileProductImageContainer: {
    width: "80px",
    height: "80px",
    flexShrink: "0",
  },
  mobileProductImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "8px",
  },
  mobileProductInfo: {
    flex: "1",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  mobileProductName: {
    margin: "0",
    fontSize: "16px",
    fontWeight: "600",
    color: "#212121",
    lineHeight: "1.3",
  },
  mobileProductFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "auto",
  },
  mobileProductPrice: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#4caf50",
  },
  mobileQuantityControl: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  mobileQuantityButton: {
    backgroundColor: "#3f51b5",
    color: "white",
    border: "none",
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "bold",
    transition: "all 0.2s ease",
  },
  mobileQuantityValue: {
    minWidth: "30px",
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
  },
  productsColumn: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "20px",
    overflow: "auto",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  },
  orderColumn: {
    backgroundColor: "rgb(254 255 226)",
    borderRadius: "12px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    display: "flex",
    flexDirection: "column",
  },
  mobileOrderColumn: {
    position: "fixed",
    bottom: "0",
    left: "0",
    right: "0",
    height: "110px",
    borderBottomLeftRadius: "0",
    borderBottomRightRadius: "0",
    borderTopLeftRadius: "20px",
    borderTopRightRadius: "20px",
    zIndex: "1000",
    padding: "15px",
    backgroundColor: "rgb(254 255 226)",
    boxShadow: "0 -5px 20px rgba(0,0,0,0.15)",
    marginBottom: "19%",
  },
  tabletOrderColumn: {
    padding: "20px",
  },
  desktopOrderColumn: {
    padding: "20px",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 0",
    color: "#757575",
  },
  loadingIcon: {
    fontSize: "24px",
    marginBottom: "15px",
  },
  categorySection: {
    marginBottom: "30px",
  },
  categoryTitle: {
    color: "#3f51b5",
    margin: "20px 0 15px 0",
    fontSize: "20px",
    fontWeight: "600",
    paddingLeft: "15px",
  },
  productsGrid: {
    display: "grid",
    gap: "15px",
  },
  mobileGrid: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  tabletGrid: {
    gridTemplateColumns: "repeat(3, 1fr)",
  },
  desktopGrid: {
    gridTemplateColumns: "repeat(4, 1fr)",
  },
  productCard: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
    transition: "all 0.3s ease",
    border: "1px solid #f0f0f0",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  productImageContainer: {
    position: "relative",
    width: "100%",
    paddingBottom: "100%",
    overflow: "hidden",
  },
  productImage: {
    position: "absolute",
    top: "0",
    left: "0",
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  quantityOverlay: {
    position: "absolute",
    bottom: "10px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "rgba(255,255,255,0.9)",
    borderRadius: "20px",
    padding: "5px 10px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
  },
  quantityControl: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  quantityButton: {
    backgroundColor: "#3f51b5",
    color: "white",
    border: "none",
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "bold",
    transition: "all 0.2s ease",
  },
  quantityValue: {
    minWidth: "20px",
    textAlign: "center",
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
  },
  productInfo: {
    padding: "15px",
    flex: "1",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    minHeight: "120px",
  },
  productName: {
    margin: "0",
    fontSize: "16px",
    fontWeight: "600",
    color: "#212121",
    lineHeight: "1.3",
    minHeight: "40px",
    display: "flex",
    alignItems: "center",
  },
  productDescription: {
    margin: "0",
    fontSize: "13px",
    color: "#757575",
    lineHeight: "1.4",
    flex: "1",
    minHeight: "40px",
  },
  readMoreButton: {
    backgroundColor: "transparent",
    border: "none",
    color: "#3f51b5",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "500",
    marginLeft: "5px",
    textDecoration: "underline",
  },
  productFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "auto",
  },
  productPrice: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#4caf50",
  },
  orderContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: "0",
  },
  orderTitle: {
    color: "#3f51b5",
    margin: "0 0 15px 0",
    fontSize: "20px",
    fontWeight: "600",
    textAlign: "center",
  },
  orderItemsScroll: {
    flex: "1",
    overflow: "auto",
    marginBottom: "15px",
    minHeight: "0",
  },
  orderItemsList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  orderItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    border: "1px solid #e9ecef",
  },
  orderItemInfo: {
    flex: "1",
  },
  orderItemName: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#212121",
    display: "block",
    marginBottom: "4px",
  },
  orderItemDetails: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  orderItemQuantity: {
    fontSize: "12px",
    color: "#757575",
    backgroundColor: "#e0e0e0",
    padding: "2px 8px",
    borderRadius: "10px",
    fontWeight: "500",
  },
  orderItemPrice: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#4caf50",
  },
  orderItemRemove: {
    backgroundColor: "transparent",
    border: "none",
    color: "#f44336",
    cursor: "pointer",
    fontSize: "14px",
    padding: "4px",
    marginLeft: "10px",
  },
  emptyMessage: {
    textAlign: "center",
    color: "#9e9e9e",
    fontSize: "14px",
    padding: "20px",
  },
  orderTotal: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "15px 0",
    borderTop: "2px solid #e0e0e0",
    marginBottom: "15px",
  },
  mobileOrderFooter: {
    display: "flex",
    gap: "12px",
    marginTop: "auto",
    marginBottom: "15px",
  },
  totalLabel: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#333",
  },
  totalAmount: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#4caf50",
  },
  checkoutButton: {
    backgroundColor: "#4caf50",
    color: "#fff",
    border: "none",
    padding: "16px 20px",
    borderRadius: "12px",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: "15px",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    transition: "all 0.3s ease",
    fontFamily: "Montserrat, sans-serif",
    marginTop: "-10px"
  },
  disabledCheckoutButton: {
    backgroundColor: "#cccccc",
    cursor: "not-allowed",
  },
  checkoutIcon: {
    fontSize: "16px",
  },
  categoryFiltersContainer: {
    padding: "0 20px 15px 20px",
    borderBottom: "1px solid #e0e0e0",
    marginBottom: "20px",
  },
  categoryFilters: {
    display: "flex",
    gap: "10px",
    overflowX: "auto",
    paddingBottom: "5px",
  },
  categoryFilterButton: {
    backgroundColor: "rgba(255, 255, 255, 1)",
    color: "#666",
    border: "none",
    padding: "10px 20px",
    borderRadius: "20px",
    fontWeight: "500",
    cursor: "pointer",
    fontSize: "14px",
    whiteSpace: "nowrap",
    flexShrink: "0",
    transition: "all 0.3s ease",
    minWidth: "fit-content",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
  },
  categoryFilterButtonActive: {
    backgroundColor: "#3f51b5",
    color: "white",
  },

  // Novos estilos para identificação de membro
  memberIndicator: {
    backgroundColor: '#e3f2fd',
    border: '1px solid #bbdefb',
    borderRadius: '8px',
    padding: '12px 20px',
    margin: '0 20px 20px 20px',
  },
  memberIndicatorContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  memberIndicatorIcon: {
    color: '#1976d2',
    fontSize: '20px',
  },
  memberIndicatorInfo: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  memberIndicatorLabel: {
    fontSize: '12px',
    color: '#666',
  },
  memberIndicatorValue: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1976d2',
  },
  identifyButton: {
    backgroundColor: '#1976d2',
    color: 'white',
    border: 'none',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },

  // Estilos do modal de identificação
  memberModalContent: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "25px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
    width: "100%",
    maxWidth: "500px",
    maxHeight: "80vh",
    overflow: "auto",
  },
  tagStatus: {
    backgroundColor: '#f5f5f5',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '20px',
    textAlign: 'center',
  },
  tagStatusIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '10px',
  },
  statusLight: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    transition: 'all 0.3s ease',
  },
  statusLightReady: {
    backgroundColor: '#4caf50',
    boxShadow: '0 0 10px #4caf50',
  },
  statusLightActive: {
    backgroundColor: '#ff9800',
    boxShadow: '0 0 10px #ff9800',
  },
  statusText: {
    fontWeight: '600',
    color: '#333',
  },
  tagData: {
    fontSize: '12px',
    color: '#666',
    fontFamily: 'monospace',
  },
  instructions: {
    backgroundColor: '#e8f5e8',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '20px',
  },
  instructionText: {
    margin: '5px 0',
    fontSize: '14px',
    color: '#2e7d32',
  },
  tagInputContainer: {
    marginBottom: '20px',
  },
  searchTitle: {
    margin: '0 0 10px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
  },

  tagInput: {
    flex: '1',
    padding: '12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px 0 0 8px',
    fontSize: '14px',
    fontFamily: 'monospace',
  },
  qrButton: {
    backgroundColor: '#3f51b5',
    color: 'white',
    border: 'none',
    padding: '12px 15px',
    borderRadius: '0 8px 8px 0',
    cursor: 'pointer',
    fontSize: '16px',
  },
  loadingResults: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '15px',
    color: '#666',
  },
  searchResults: {
    maxHeight: '200px',
    overflow: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    marginTop: '15px',
  },
  resultsTitle: {
    margin: '10px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
  },
  searchResultItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },
  searchResultItemHover: {
    backgroundColor: '#f5f5f5',
  },
  resultIcon: {
    color: '#3f51b5',
    fontSize: '16px',
  },
  resultInfo: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  resultName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
  },
  resultId: {
    fontSize: '12px',
    color: '#666',
  },
  resultTag: {
    fontSize: '11px',
    color: '#999',
    fontFamily: 'monospace',
  },
  resultBalance: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#4caf50',
  },
  cancelButton: {
    backgroundColor: '#f5f5f5',
    color: '#757575',
    border: 'none',
    padding: '12px 20px',
    borderRadius: '8px',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '14px',
    flex: '1',
  },
  testButton: {
    backgroundColor: '#ff9800',
    color: 'white',
    border: 'none',
    padding: '12px 20px',
    borderRadius: '8px',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '14px',
    flex: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  memberInfoSection: {
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
  },
  memberInfoTitle: {
    margin: '0 0 10px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
  },
  memberInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  memberIcon: {
    fontSize: '20px',
    color: '#3f51b5',
  },
  memberDetails: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  memberName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
  },
  memberId: {
    fontSize: '12px',
    color: '#666',
  },
  memberTag: {
    fontSize: '11px',
    color: '#999',
    fontFamily: 'monospace',
  },
  memberBalance: {
    fontSize: '12px',
    color: '#4caf50',
    fontWeight: '500',
  },
  changeMemberButton: {
    backgroundColor: 'transparent',
    color: '#3f51b5',
    border: '1px solid #3f51b5',
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  noMemberSelected: {
    textAlign: 'center',
  },
  noMemberText: {
    margin: '0 0 10px 0',
    color: '#666',
    fontSize: '14px',
  },
  selectMemberButton: {
    backgroundColor: '#3f51b5',
    color: 'white',
    border: 'none',
    padding: '10px 15px',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  // Estilos do modal de QR Code
  qrModalContent: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "25px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
    width: "100%",
    maxWidth: "500px",
    maxHeight: "80vh",
    overflow: "auto",
  },
  qrScannerContainer: {
    marginBottom: '20px',
  },
  cameraPreview: {
    position: 'relative',
    width: '100%',
    height: '300px',
    backgroundColor: '#000',
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: '15px',
  },
  cameraVideo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  qrOverlay: {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  qrFrame: {
    width: '200px',
    height: '200px',
    border: '2px solid #4caf50',
    borderRadius: '8px',
    marginBottom: '15px',
  },
  qrInstruction: {
    color: 'white',
    textAlign: 'center',
    fontSize: '14px',
    margin: '0',
    textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
  },
  cameraError: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '10px',
    borderRadius: '4px',
    textAlign: 'center',
    fontSize: '14px',
  },
  qrModalActions: {
    display: 'flex',
    gap: '15px',
  },
  scanButton: {
    backgroundColor: '#4caf50',
    color: 'white',
    border: 'none',
    padding: '12px 20px',
    borderRadius: '8px',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '14px',
    flex: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
};

// Adicionar animações CSS
const styleSheet = document.styleSheets[0];
styleSheet.insertRule(`
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`, styleSheet.cssRules.length);

styleSheet.insertRule(`
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`, styleSheet.cssRules.length);

export default PedidoColaborador;